
import React, { useState, useEffect, useMemo } from "react";
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { getAssessmentIdNameMap, addStudentInfo, StudentInfo } from "./StudentInfo_APIs";
import { GetSessionsByInstituteCode } from "../College/API/College_APIs";

interface CreateStudentModalProps {
  show: boolean;
  onHide: () => void;
  onSave?: (data: any) => void;
  /**
   * Optional institute override. When omitted the modal falls back to the
   * `instituteId` stored in localStorage (legacy StudentsList behaviour).
   * Passed from contexts where the user picks an institute in-page (e.g.,
   * the bulk-upload page) so the modal scopes its session/class/section
   * cascade to the right school.
   */
  instituteId?: number | string;
}

const CreateStudentModal: React.FC<CreateStudentModalProps> = ({ show, onHide, onSave, instituteId }) => {
  const [formData, setFormData] = useState({
    name: "",
    schoolRollNumber: "",
    controlNumber: "",
    phoneNumber: "",
    dob: "",
    selectedAssessmentId: "",
    careerNineRollNumber: "",
  });
  const [autoGenerateRoll, setAutoGenerateRoll] = useState(true);

  const [assessmentOptions, setAssessmentOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Session/Class/Section cascading state
  const [hierarchyData, setHierarchyData] = useState<any[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");

  const effectiveInstituteId = useMemo(
    () => (instituteId != null && instituteId !== "" ? String(instituteId) : localStorage.getItem('instituteId')),
    [instituteId]
  );

  useEffect(() => {
    if (show) {
      setLoading(true);

      const promises: Promise<any>[] = [
        getAssessmentIdNameMap(),
      ];

      if (effectiveInstituteId) {
        promises.push(GetSessionsByInstituteCode(effectiveInstituteId));
      }

      Promise.all(promises)
        .then(([assessmentRes, sessionRes]) => {
          const options = Object.entries(assessmentRes.data).map(([id, name]) => ({
            id,
            name: name as string,
          }));
          setAssessmentOptions(options);

          if (sessionRes) {
            setHierarchyData(sessionRes.data || []);
          }
        })
        .catch((err) => console.error("Failed to load data", err))
        .finally(() => setLoading(false));
    }
  }, [show, effectiveInstituteId]);

  const classes = useMemo(() => {
    if (!selectedSessionId) return [];
    const session = hierarchyData.find((s: any) => String(s.id) === selectedSessionId);
    return session?.schoolClasses || [];
  }, [hierarchyData, selectedSessionId]);

  const sections = useMemo(() => {
    if (!selectedClassId) return [];
    const cls = classes.find((c: any) => String(c.id) === selectedClassId);
    return cls?.schoolSections || [];
  }, [classes, selectedClassId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Helper to format date YYYY-MM-DD -> DD-MM-YYYY
  const formatDateForBackend = (dateString: string): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.name || !formData.dob || !formData.selectedAssessmentId || !selectedSectionId) {
      showErrorToast("Please fill in all required fields (Name, DOB, Assessment, Section)");
      return;
    }
    if (!autoGenerateRoll && !formData.careerNineRollNumber.trim()) {
      showErrorToast("Please enter a roll number or switch to auto-generate");
      return;
    }

    setSubmitting(true);
    try {
        const payload: StudentInfo = {
            name: formData.name,
            schoolRollNumber: formData.schoolRollNumber || "",
            controlNumber: formData.controlNumber ? Number(formData.controlNumber) : undefined,
            phoneNumber: formData.phoneNumber ? Number(formData.phoneNumber) : undefined,
            email: "",
            address: "",
            studentDob: formatDateForBackend(formData.dob),
            instituteId: effectiveInstituteId ? Number(effectiveInstituteId) : undefined,
            assesment_id: formData.selectedAssessmentId,
            schoolSectionId: Number(selectedSectionId),
            careerNineRollNumber: autoGenerateRoll ? "" : formData.careerNineRollNumber,
        };

        await addStudentInfo(payload);

        showSuccessToast("Student added successfully!");

        if (onSave) {
            onSave(payload);
        }
        onHide();
    } catch (error) {
      console.error("Error creating student:", error);
      showErrorToast("Failed to create student");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add New Student</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading...</p>
          </div>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Student Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter full name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Career-Nine Roll Number <span className="text-danger">*</span></Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Check
                  type="switch"
                  id="auto-generate-roll"
                  label="Auto-generate"
                  checked={autoGenerateRoll}
                  onChange={(e) => {
                    setAutoGenerateRoll(e.target.checked);
                    if (e.target.checked) {
                      setFormData((prev) => ({ ...prev, careerNineRollNumber: "" }));
                    }
                  }}
                />
              </div>
              {autoGenerateRoll ? (
                <Form.Control
                  type="text"
                  disabled
                  placeholder="Will be auto-generated from section (e.g., A001)"
                  className="bg-light"
                />
              ) : (
                <Form.Control
                  type="text"
                  name="careerNineRollNumber"
                  value={formData.careerNineRollNumber}
                  onChange={handleChange}
                  placeholder="Enter roll number manually"
                />
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>School Roll Number</Form.Label>
              <Form.Control
                type="text"
                name="schoolRollNumber"
                value={formData.schoolRollNumber}
                onChange={handleChange}
                placeholder="Enter school roll number (optional)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Control Number</Form.Label>
              <Form.Control
                type="number"
                name="controlNumber"
                value={formData.controlNumber}
                onChange={handleChange}
                placeholder="Enter control number (optional)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Enter phone number (optional)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Format: DD-MM-YYYY (Handled by date picker)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Session <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={selectedSessionId}
                onChange={(e) => {
                  setSelectedSessionId(e.target.value);
                  setSelectedClassId("");
                  setSelectedSectionId("");
                }}
              >
                <option value="">-- Select Session --</option>
                {hierarchyData.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.sessionYear}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Class <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={selectedClassId}
                onChange={(e) => {
                  setSelectedClassId(e.target.value);
                  setSelectedSectionId("");
                }}
                disabled={!selectedSessionId}
              >
                <option value="">-- Select Class --</option>
                {classes.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.className}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Section <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={selectedSectionId}
                onChange={(e) => setSelectedSectionId(e.target.value)}
                disabled={!selectedClassId}
              >
                <option value="">-- Select Section --</option>
                {sections.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.sectionName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Assign Assessment <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="selectedAssessmentId"
                value={formData.selectedAssessmentId}
                onChange={handleChange}
              >
                <option value="">-- Select Assessment --</option>
                {assessmentOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || loading}
            style={{
                background: 'linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%)',
                border: 'none'
            }}
        >
          {submitting ? 'Saving...' : 'Add Student'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateStudentModal;
